/**
 * Authentication Context Provider
 * Provides authentication state and user information throughout the app
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  useMsal,
  useIsAuthenticated,
  useMsalAuthentication,
} from '@azure/msal-react';
import {
  InteractionType,
  InteractionStatus,
} from '@azure/msal-browser';
import {
  loginRequest,
  AZURE_AD_GROUPS,
  ROLE_HIERARCHY,
  UserRole,
  TSM_STORE_GROUPS,
  resolveTsmLocationsFromGroups,
  tsmDepartmentForLocation,
} from './msalConfig';
import { setTokenGetter } from '../services/api';
import { setTokenGetter as setProjectionsTokenGetter } from '../services/projectionsApi';
import { setTokenGetter as setNutSheetTokenGetter } from '../services/nutSheetApi';
import { setAuditTokenGetter, logAuthEvent } from '../services/auditApi';
import { setUsersTokenGetter, getUserAssignment } from '../services/usersApi';
import { setAgentKeysTokenGetter } from '../services/agentKeysApi';
import { setInsuranceTokenGetter } from '../services/insuranceApi';
import { setAIChatTokenGetter } from '../services/aiChatApi';

/**
 * User information extracted from Azure AD
 */
export interface AuthUser {
  id: string;              // Azure AD Object ID
  email: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  role: UserRole;
  groupIds: string[];      // Azure AD group IDs user belongs to
  accessToken?: string;    // For API calls
}

/**
 * User assignment from DynamoDB (location/department access)
 */
export interface UserAssignment {
  locationIds: number[];
  departmentIds?: string[];
}

/**
 * Authentication context value
 */
interface AuthContextValue {
  user: AuthUser | null;
  userRole: UserRole | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  hasRole: (requiredRole: UserRole) => boolean;
  isAdmin: boolean;
  isExecutive: boolean;
  isGeneralManager: boolean;
  isDepartmentManager: boolean;
  isTeamServiceManager: boolean; // TSM: Forecast-only, auto-assigned location(s) and department(s)
  isInsuranceAgent: boolean;  // Insurance-only user (no access to other dashboard features)
  isMarketingManager: boolean; // Marketing-only user (Reports page, Marketing tab only)
  // Location and department assignment for DeptMgr and TSM
  assignedLocationIds: number[];
  assignedDepartmentIds: string[];
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Hook to access authentication context
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Authentication Provider Component
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assignedLocationIds, setAssignedLocationIds] = useState<number[]>([]);
  const [assignedDepartmentIds, setAssignedDepartmentIds] = useState<string[]>([]);

  // Attempt silent authentication on mount
  useMsalAuthentication(InteractionType.Silent, loginRequest);

  /**
   * Extract group IDs from token claims
   * Groups are included in the ID token when configured in Azure AD App Registration
   * (Token configuration -> Add groups claim)
   */
  const extractGroupsFromToken = useCallback((idTokenClaims: any): string[] => {
    // Groups can be in 'groups' claim as array of GUIDs
    if (idTokenClaims?.groups && Array.isArray(idTokenClaims.groups)) {
      return idTokenClaims.groups;
    }

    // If user is in too many groups (>200), Azure returns _claim_names instead
    // In this case, we'd need to call Graph API, but for now return empty
    if (idTokenClaims?._claim_names?.groups) {
      console.warn('User has too many groups for token claims. Groups not available.');
      return [];
    }

    return [];
  }, []);

  /**
   * Determine user's role based on Azure AD group memberships
   * Returns the highest-privilege role if user belongs to multiple groups
   * Hierarchy: Admin > CEO > Executive > GeneralManager > InsuranceManager > MarketingManager > DepartmentManager > Insurance > Unassigned
   * Insurance Manager overrides DepartmentManager (checked before DeptMgr)
   * Marketing Manager overrides DepartmentManager (checked before DeptMgr)
   * Regular Insurance agents do NOT override DepartmentManager (checked after DeptMgr)
   */
  const determineRole = useCallback((groupIds: string[]): UserRole => {
    // Check groups in order of privilege (highest first)
    if (AZURE_AD_GROUPS.ADMINS.some(g => groupIds.includes(g))) {
      return 'Admin';
    }
    if (AZURE_AD_GROUPS.CEO.some(g => groupIds.includes(g))) {
      return 'CEO';
    }
    if (AZURE_AD_GROUPS.EXECUTIVES.some(g => groupIds.includes(g))) {
      return 'Executive';
    }
    if (AZURE_AD_GROUPS.GENERAL_MANAGERS.some(g => groupIds.includes(g))) {
      return 'GeneralManager';
    }
    // Insurance Manager overrides DepartmentManager
    if (AZURE_AD_GROUPS.INSURANCE_MANAGERS.some(g => groupIds.includes(g))) {
      return 'Insurance';
    }
    // Marketing Manager overrides DepartmentManager
    if (AZURE_AD_GROUPS.MARKETING_MANAGERS.some(g => groupIds.includes(g))) {
      return 'MarketingManager';
    }
    // Accounting: single-purpose Nut Sheet role. Dedicated group, no overlap expected;
    // any higher-privilege group (Admin/Exec/GM) already returned above.
    if (AZURE_AD_GROUPS.ACCOUNTING.some(g => groupIds.includes(g))) {
      return 'Accounting';
    }
    if (AZURE_AD_GROUPS.DEPARTMENT_MANAGERS.some(g => groupIds.includes(g))) {
      return 'DepartmentManager';
    }
    // Finance (F&I) Manager: single-purpose Nut Sheet role, location-scoped.
    // Checked AFTER DepartmentManager so a user in both groups keeps their broader
    // DepartmentManager access (Forecast/Projections + dept scope), not the Nut-Sheet-only
    // F&I role. A user is FinanceManager only if they are NOT a DepartmentManager.
    if (AZURE_AD_GROUPS.FINANCE_MANAGERS.some(g => groupIds.includes(g))) {
      return 'FinanceManager';
    }
    // Regular Insurance agents (does not override DeptMgr)
    if (AZURE_AD_GROUPS.INSURANCE_AGENTS.some(g => groupIds.includes(g))) {
      return 'Insurance';
    }
    // Team Service Manager via the All TSMs Entra group.
    // Checked after all higher-privilege groups so a TSM who is also in (say) DEPARTMENT_MANAGERS still gets DeptMgr.
    if (AZURE_AD_GROUPS.TEAM_SERVICE_MANAGERS.some(g => groupIds.includes(g))) {
      return 'TeamServiceManager';
    }
    return 'Unassigned';
  }, []);

  /**
   * Get the ID token for API calls
   * Our Lambda authorizer validates Azure AD ID tokens directly
   * @param forceRefresh - If true, bypasses MSAL cache and gets a fresh token from Azure AD
   */
  const getAccessToken = useCallback(async (forceRefresh: boolean = false): Promise<string | null> => {
    if (!accounts[0]) return null;

    try {
      // Get the ID token directly from the cached account
      // The ID token is already available after login
      const account = accounts[0];

      // Try to get a fresh token silently
      // When forceRefresh is true, bypass MSAL's token cache to get a new token
      const response = await instance.acquireTokenSilent({
        scopes: ['openid'],  // Need at least openid to get idToken
        account: account,
        forceRefresh: forceRefresh,  // Bypass cache when token was rejected
      });

      console.log('Token acquired, idToken present:', !!response.idToken, forceRefresh ? '(forced refresh)' : '');
      return response.idToken;
    } catch (err) {
      console.error('Silent token acquisition failed:', err);
      // If silent token acquisition fails, try interactive
      try {
        const response = await instance.acquireTokenPopup({
          scopes: ['openid'],
        });
        return response.idToken;
      } catch (popupErr) {
        console.error('Failed to acquire token:', popupErr);
        return null;
      }
    }
  }, [instance, accounts]);

  // Connect token getter to API services
  useEffect(() => {
    setTokenGetter(getAccessToken);
    setProjectionsTokenGetter(getAccessToken);
    setNutSheetTokenGetter(getAccessToken);
    setAuditTokenGetter(getAccessToken);
    setUsersTokenGetter(getAccessToken);
    setAgentKeysTokenGetter(getAccessToken);
    setInsuranceTokenGetter(getAccessToken);
    setAIChatTokenGetter(getAccessToken);
  }, [getAccessToken]);

  // Track if we've logged this session's login event
  const hasLoggedLogin = useRef(false);

  /**
   * Load user data when authenticated
   * Groups are extracted from ID token claims (no Graph API call needed)
   */
  useEffect(() => {
    const loadUser = async () => {
      if (!isAuthenticated || !accounts[0]) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const account = accounts[0];

        // Extract groups from ID token claims (configured in Azure AD App Registration)
        const groupIds = extractGroupsFromToken(account.idTokenClaims);

        // Read custom claims from Enterprise App Attributes & Claims configuration
        const jobTitle = (account.idTokenClaims as any)?.JobTitle || (account.idTokenClaims as any)?.jobTitle;
        const company = (account.idTokenClaims as any)?.Location || (account.idTokenClaims as any)?.location;

        const role = determineRole(groupIds);

        // Get access token for our API
        const accessToken = await getAccessToken();

        const authUser: AuthUser = {
          id: account.localAccountId,
          email: account.username,
          displayName: account.name || account.username,
          firstName: (account.idTokenClaims as any)?.given_name,
          lastName: (account.idTokenClaims as any)?.family_name,
          role,
          groupIds,
          accessToken: accessToken || undefined,
        };

        console.log('User loaded from token claims:', {
          email: authUser.email,
          role: authUser.role,
          groupCount: groupIds.length,
          jobTitle,
          company,
        });

        setUser(authUser);

        // Fetch location assignment for DepartmentManager and FinanceManager (F&I).
        // This determines which locations they can see. FinanceManager has no department
        // gate, so its assignedDepartmentIds are expected to be empty.
        if (role === 'DepartmentManager' || role === 'FinanceManager') {
          try {
            const assignmentResp = await getUserAssignment();
            if (assignmentResp.success && assignmentResp.data) {
              setAssignedLocationIds(assignmentResp.data.assignedLocationIds || []);
              setAssignedDepartmentIds(assignmentResp.data.assignedDepartmentIds || []);
              console.log(`${role} assignment loaded:`, {
                locations: assignmentResp.data.assignedLocationIds,
                departments: assignmentResp.data.assignedDepartmentIds,
              });
            } else {
              // No assignment yet. DeptMgr sees all until assigned; FinanceManager (location-
              // scoped, no all-locations fallback) sees nothing until an admin assigns a location.
              setAssignedLocationIds([]);
              setAssignedDepartmentIds([]);
              console.log(`${role} has no assignment yet`);
            }
          } catch (assignErr) {
            console.warn('Failed to load assignment:', assignErr);
            setAssignedLocationIds([]);
            setAssignedDepartmentIds([]);
          }
        } else if (role === 'TeamServiceManager') {
          // Auto-derive all locations and their departments from the store service groups.
          const locationIds = resolveTsmLocationsFromGroups(groupIds);
          const matchedStoreGroups = Array.from(new Set(groupIds.filter(group => TSM_STORE_GROUPS[group])))
            .map(group => TSM_STORE_GROUPS[group].name);
          if (locationIds.length > 0) {
            const departments = Array.from(new Set(
              locationIds.map(locationId => tsmDepartmentForLocation(locationId))
            )).sort((a, b) => (a === 'SERVICE' ? -1 : b === 'SERVICE' ? 1 : 0));
            setAssignedLocationIds(locationIds);
            setAssignedDepartmentIds(departments);
            console.log('TSM assignment auto-derived:', {
              locationIds,
              matchedStoreGroups,
              departments,
            });
          } else {
            console.warn('TSM store group did not resolve to a location', { groupIds });
            setAssignedLocationIds([]);
            setAssignedDepartmentIds(['SERVICE']);
          }
        } else if (role === 'MarketingManager') {
          // Marketing Manager: Reports page only, Marketing tab only
          setAssignedLocationIds([]);
          setAssignedDepartmentIds(['MARKETING']);
          console.log('MarketingManager assignment: MARKETING tab only');
        } else if (role === 'Accounting') {
          // Accounting: Nut Sheet page only, all locations (empty = unrestricted, like GM)
          setAssignedLocationIds([]);
          setAssignedDepartmentIds([]);
          console.log('Accounting assignment: Nut Sheet only, all locations');
        } else {
          // Non-DeptMgr/TSM/MarketingMgr users see all locations/departments
          setAssignedLocationIds([]);
          setAssignedDepartmentIds([]);
        }

        // Log auth event (only once per session)
        // Log ACCESS_DENIED for Unassigned users, LOGIN for users with valid roles
        if (!hasLoggedLogin.current) {
          hasLoggedLogin.current = true;
          const authAction = role === 'Unassigned' ? 'ACCESS_DENIED' : 'LOGIN';
          // Fire and forget - don't block user loading
          logAuthEvent(authAction).catch(err => {
            console.warn('Failed to log auth event:', err);
          });
        }
      } catch (err: any) {
        console.error('Failed to load user:', err);
        setError(err.message || 'Failed to load user information');
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    // Only load user when interaction is complete
    if (inProgress === InteractionStatus.None) {
      loadUser();
    }
  }, [
    isAuthenticated,
    accounts,
    inProgress,
    extractGroupsFromToken,
    determineRole,
    getAccessToken,
  ]);

  /**
   * Initiate login
   */
  const login = useCallback(async () => {
    try {
      setError(null);
      await instance.loginRedirect(loginRequest);
    } catch (err: any) {
      console.error('Login failed:', err);
      setError(err.message || 'Login failed');
    }
  }, [instance]);

  /**
   * Initiate logout
   */
  const logout = useCallback(async () => {
    try {
      // Log logout event before redirect
      try {
        await logAuthEvent('LOGOUT');
      } catch (auditErr) {
        console.warn('Failed to log logout event:', auditErr);
      }

      setUser(null);
      hasLoggedLogin.current = false;
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (err: any) {
      console.error('Logout failed:', err);
      setError(err.message || 'Logout failed');
    }
  }, [instance]);

  /**
   * Check if user has the required role or higher
   */
  const hasRole = useCallback(
    (requiredRole: UserRole): boolean => {
      if (!user) return false;
      const userRoleIndex = ROLE_HIERARCHY.indexOf(user.role);
      const requiredRoleIndex = ROLE_HIERARCHY.indexOf(requiredRole);
      // Lower index = higher privilege
      return userRoleIndex <= requiredRoleIndex && userRoleIndex !== -1;
    },
    [user]
  );

  // Convenience role checks (includes higher-privilege roles)
  const isAdmin = user?.role === 'Admin';
  const isCEO = user?.role === 'CEO';
  const isExecutive = user?.role === 'Executive' || isCEO || isAdmin;
  const isGeneralManager = user?.role === 'GeneralManager' || isExecutive;
  const isDepartmentManager = user?.role === 'DepartmentManager' || isGeneralManager;
  const isTeamServiceManager = user?.role === 'TeamServiceManager';
  const isInsuranceAgent = user?.role === 'Insurance';  // Insurance-only users
  const isMarketingManager = user?.role === 'MarketingManager'; // Marketing-only users

  // Still loading if MSAL is processing or we're fetching user data
  const loading = inProgress !== InteractionStatus.None || isLoading;

  const value: AuthContextValue = {
    user,
    userRole: user?.role || null,
    isAuthenticated,
    isLoading: loading,
    error,
    login,
    logout,
    getAccessToken,
    hasRole,
    isAdmin,
    isExecutive,
    isGeneralManager,
    isDepartmentManager,
    isTeamServiceManager,
    isInsuranceAgent,
    isMarketingManager,
    assignedLocationIds,
    assignedDepartmentIds,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
